import { createContext, useContext } from 'react';

import { cn } from '@design-system/helpers';
import { Spinner } from '@design-system/spinner';
import type { ComponentProps } from 'react';

type TStateBoundaryContext = {
  isLoading?: boolean;
  isError?: boolean;
  isNotFound?: boolean;
};

const StateBoundaryContext = createContext<TStateBoundaryContext>({});

export function StateBoundary({
  isLoading,
  isError,
  isNotFound,
  children,
}: TStateBoundaryContext & {
  children: React.ReactNode;
}) {
  return (
    <StateBoundaryContext.Provider value={{ isLoading, isError, isNotFound }}>
      {children}
    </StateBoundaryContext.Provider>
  );
}

export const StateBoundaryError = ({ ...props }: ComponentProps<'div'>) => {
  const { isError } = useContext(StateBoundaryContext);

  return isError && <div {...props} />;
};

export const StateBoundaryLoading = ({
  className,
  children,
  ...props
}: ComponentProps<'div'>) => {
  const { isLoading } = useContext(StateBoundaryContext);

  return (
    isLoading && (
      <div
        className={cn('flex items-center justify-center', className)}
        {...props}
      >
        {children || <Spinner className='size-8' />}
      </div>
    )
  );
};

export const StateBoundaryContent = ({ ...props }: ComponentProps<'div'>) => {
  const { isNotFound, isError, isLoading } = useContext(StateBoundaryContext);

  return !isNotFound && !isError && !isLoading && <div {...props} />;
};

export function StateBoundaryNotFound({
  children,
  ...props
}: ComponentProps<'div'>) {
  const { isNotFound, isError, isLoading } = useContext(StateBoundaryContext);

  return (
    isNotFound && !isError && !isLoading && <div {...props}>{children}</div>
  );
}
